import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../config/AppContext";
import {
  getDocDataById,
  getDocDataByRef,
  getFlutterTime,
  queryCollection,
} from "../../models/DataFunctions";
import { getCommunWorkspace } from "./CarnetHelpers";
import { firstPlan } from "../../hexagone/VisibilityHelpers";
import dayjs from "dayjs";
import { Divider, Spin } from "antd";
import DropDownGPT03 from "../../dropdowns/DropDownGTP03";
import TextField2T from "../../textfields/TextField2T";

import SecondaryButton from "../../buttons/SecondaryButton";
import PrimaryButton from "../../buttons/PrimaryButton";
import SmallSpinner from "../../loading/SmallSpinner";
import createDocApi from "../../api/database/createDocApi";
import { User } from "../../types/user";
import { Org } from "../../types/org";
import Space from "../../layout/Space";
import ConstructorSection from "../../components/ConstructorSection";

interface NewCarnetProps {
  userData: User; // Replace 'any' with the correct type if available
  orgData: Org; // Replace 'any' with the correct type if available
  navigateWorkspace: (path: string) => void;
}

function NewCarnet({ userData, orgData, navigateWorkspace }: NewCarnetProps) {
  const { currentUser, loading } = useAuth();

  const intervention_options = [
    { value: false, label: "Intervention à venir" },
    { value: true, label: "Intervention réalisée" },
  ];

  const [step, setStep] = useState(0);

  const [structure_options, setStructure_options] = useState([
    { value: "maison_individuelle", label: "Maison individuelle" },
    { value: "extension", label: "Extension" },
    { value: "collectif", label: "Collectif" },
    { value: "tertiaire", label: "Tertiaire" },
  ]);
  const [structure, setStructure] = useState(structure_options[0].value);
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (
    message: string,
    variant: "success" | "error" | "default" | "warning" | "info" | undefined
  ) => {
    enqueueSnackbar(message, { variant });
  };

  // State variables to store the input values
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [num, setNum] = useState("");
  const [city, setcity] = useState("");
  const [address, setAddress] = useState("");
  const [postal, setPostal] = useState("");
  const [intervention, setIntervention] = useState(
    intervention_options[0].value
  );

  const [mission, setMission] = useState(null);
  const [conductor, setConductor] = useState(null);

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const [payment_allowed, setPaymentAllowed] = useState(false);

  const [selectedDateSeconds, setSelectedDateSeconds] = useState();
  const [selectedTime, setSelectedTime] = useState(null);

  const [loadingData, setLoadingData] = useState(false);

  const [particulier_email, setParticulierEmail] = useState("");
  const [particulier_phone, setParticulierPhone] = useState("");
  const [constructorData, setConstructorData] = useState<any | null>(null);

  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    if (currentUser) {
      try {
        const starttime = new Date();
        // Fetch user data

        // Fetch organization data using user's org reference

        const endtime = new Date();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const handleContinue = async () => {
    if (nom === "") {
      handleClickVariant("Veuillez saisir le nom du maître d'ouvrage", "error");
      return false;
    }
    if (prenom === "" && payment_allowed) {
      handleClickVariant(
        "Veuillez saisir le prénom du maître d'ouvrage",
        "error"
      );
      return false;
    }
    if (city === "") {
      handleClickVariant("Veuillez saisir la city", "error");
      return false;
    }
    if (postal === "") {
      handleClickVariant("Veuillez saisir le code postal", "error");
      return false;
    }
    if (postal.length !== 5) {
      handleClickVariant("Le code postal doit contenir 5 chiffres", "error");
      return false;
    }

    if (!particulier_email.trim().match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      handleClickVariant("Veuillez saisir une adresse email valide.", "error");
      return false;
    }
    if (particulier_phone === "") {
      handleClickVariant("Veillez saisir le téléphone du particulier", "error");
      return false;
    }

    const filters = [
      {
        field: "master_id",
        operator: "==",
        value: orgData.id,
      },
      {
        field: "name",
        operator: "==",
        value: nom,
      },
      {
        field: "postal",
        operator: "==",
        value: postal,
      },
    ];
    const carnets_with_same_data = (await queryCollection(
      "carnet",
      filters
    )) as any;
    if (carnets_with_same_data.length > 0) {
      handleClickVariant(
        "Un carnet avec les mêmes informations existe déjà",
        "error"
      );
      return false;
    }
  };

  useEffect(() => {
    if (currentUser) {
      handleFetchData(); // Fetch user data when currentUser is available
    }
  }, [currentUser]); // Dependency array ensures this runs when currentUser changes

  const createFolder = async () => {
    if (!userData || !orgData) return;

    let can_continue = await handleContinue();

    if (can_continue === false) {
      return;
    }

    try {
      setLoadingData(true);

      // If constructor data is provided and has a name, check if we need to create a partner fiche
      if (constructorData && constructorData.name && constructorData.name.trim()) {
        try {
          // Check if a partner fiche with this name already exists
          const filters = [
            { field: "org_id", operator: "==", value: orgData.id },
            { field: "type", operator: "==", value: "bookshelf_partner" },
            { field: "entreprise", operator: "==", value: constructorData.name.trim() }
          ];
          const existingFiches = await queryCollection("logement_bookshelf", filters);

          // If no fiche exists, create one
          if (!existingFiches || existingFiches.length === 0) {
            const partnerFicheData = {
              entreprise: constructorData.name,
              email: constructorData.email || "",
              tel: constructorData.phone || "",
              city: constructorData.city || "",
              adresse: constructorData.address || "",
              code_postal: constructorData.postal || "",
              org_id: orgData.id,
              type: "bookshelf_partner"
            };
            await createDocApi("logement_bookshelf", partnerFicheData, prefix);
            console.log("Partner fiche created automatically");
          }
        } catch (error) {
          console.error("Error creating partner fiche:", error);
          // Continue with carnet creation even if fiche creation fails
        }
      }

      // HL + random string
      const randomString = Math.random().toString(36).substring(2, 8);
      const code = "HL-" + randomString.toUpperCase();

      // Prepare carnet data
      const carnetData: any = {
        name: nom.trim(),
        first_name: prenom.trim(),
        num: num.trim(),
        address: address.trim(),
        city: city.trim(),
        postal: postal.trim(),
        email: particulier_email,
        phone: particulier_phone,
        master_id: orgData.id,
        creation_date: getFlutterTime(),
        code: code,
        documents_id: [],
        equipments_id: [],
        visions: [
          {
            org_id: orgData.id,
            state: "current",
          },
        ],
      };

      // Add constructor info if provided
      if (constructorData) {
        carnetData.proclient_infos = constructorData;
      }

      let new_carnet = (await createDocApi(
        "carnet",
        carnetData,
        prefix
      )) as any;
      handleClickVariant("Carnet créé avec succès", "success");
      navigateWorkspace("/carnet/" + new_carnet.id);

      setLoadingData(false);
    } catch (error) {
      console.error("Error creating folder:", error);
      setLoadingData(false);
    }
  };
  // loading || !currentUser || !userData
  if (loading || !currentUser || !userData) {
    return (
      <div className="h-full w-c400 pl-4 flex flex-col gap-2">
        <SmallSpinner />
      </div>
    );
  }

  return (
    <div className="w-full  flex-row h-full overflow-auto flex pb-20">
      {/* Left Column: Takes all available space */}

      {/* Right Column: Fixed width */}
      {step === 0 && (
        <div className="h-fit pl-4 flex flex-col gap-2 w-full max-w-c500 pb-20">
          <div className="text-h1 font-semibold">Nouveau Carnet</div>{" "}
          <div className="text-h3 text-shadow_50 w-c400">
            Renseignez les informations requises pour créer le carnet
            d'information du logement
          </div>
          <div className="h-fit  flex flex-col gap-2 w-full pr-2 mt-4">
            <TextField2T
              className="w-full"
              label="Email du particulier"
              value={particulier_email}
              onChange={(e) => setParticulierEmail(e.target.value)}
              transparent={false}
              mandatory={true}
              type="email"
            ></TextField2T>
            <TextField2T
              className="w-full"
              label="Téléphone du particulier"
              value={particulier_phone}
              onChange={(e) => setParticulierPhone(e.target.value)}
              transparent={false}
              mandatory={true}
              type="phone"
            ></TextField2T>

            <TextField2T
              className="w-full"
              label="Numéro de carnet"
              value={num}
              onChange={(e) => setNum(e.target.value)}
              transparent={false}
            ></TextField2T>

            <TextField2T
              className="w-full"
              label="Prénom du maître d'ouvrage"
              value={prenom}
              mandatory={payment_allowed}
              onChange={(e) => setPrenom(e.target.value)}
              transparent={false}
            ></TextField2T>
            <TextField2T
              className="w-full"
              label="Nom du maître d'ouvrage"
              value={nom}
              mandatory={true}
              onChange={(e) => setNom(e.target.value)}
              transparent={false}
            ></TextField2T>
            <TextField2T
              label="Code Postal"
              value={postal}
              mandatory={true}
              onChange={(e) => setPostal(e.target.value)}
              transparent={false}
            ></TextField2T>
            <TextField2T
              label="Ville"
              value={city}
              mandatory={true}
              onChange={(e) => setcity(e.target.value)}
              transparent={false}
            ></TextField2T>
            <TextField2T
              label="Adresse"
              value={address}
              mandatory={true}
              onChange={(e) => setAddress(e.target.value)}
              transparent={false}
            ></TextField2T>
          </div>
          <Space y={20} />
          <ConstructorSection
            orgId={orgData.id}
            onConstructorChange={setConstructorData}
          />
          <Space y={10} />
          <div className="w-full flex flex-row justify-end gap-2">
            <SecondaryButton disabled={loadingData} onClick={() => setStep(0)}>
              Annuler
            </SecondaryButton>
            <PrimaryButton onClick={createFolder} disabled={loadingData}>
              <div className="flex flex-row content-center align-middle items-center">
                Générer <Space x={5} />
                <SmallSpinner loading={loadingData} margin={0} size={20} />
              </div>
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewCarnet;
